import { ACTION_EVENT_QUERY_KEYS } from 'app/modules/actionEvents/queries/keys';
import { getWithParsedError, post } from 'app/shared/utils/fetchr';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectHasReadEventsPermission } from 'app/modules/session/selectors';
import {
  CachedActionEventResponse,
  ShortActionEventResponse,
} from 'app/modules/actionEvents/responses';
import { selectDLEnabledForActionEventDetail } from 'app/shared/featureFlags/selectors';
import { CGDOLoadingResponse } from 'app/modules/investigations/types/responses';
import {
  getLoadingFromColdStorageStateForCGDO,
  getColdStorageRetryWaitTime,
  COLD_STORAGE_MAX_RETRIES,
  isCGDOLoadingResponse,
} from 'app/shared/utils/fastArchHelpers';
import { useCGDOProgressiveData } from 'app/shared/hooks/useCGDOProgressiveData';
import { useRef, useMemo, useEffect } from 'react';

type ActionEventResponse =
  | CachedActionEventResponse
  | ShortActionEventResponse
  | (CachedActionEventResponse & CGDOLoadingResponse)
  | CGDOLoadingResponse;

export const useGetActionEventDetail = (id: string) => {
  const hasReadEventsPermission = useSelector(selectHasReadEventsPermission);
  const useNewEndpoint = useSelector(selectDLEnabledForActionEventDetail);
  const dataUpdateCountRef = useRef(0);

  const query = useQuery<ActionEventResponse, Error, ActionEventResponse>({
    queryFn: async () => {
      if (useNewEndpoint) {
        return getWithParsedError<
          | CachedActionEventResponse
          | (CachedActionEventResponse & CGDOLoadingResponse)
        >(`/action-events/cached/${encodeURIComponent(id)}`);
      }
      return post<ShortActionEventResponse>(`/action-events/get`, {
        action_event_id: `${id}`,
      });
    },
    queryKey: ACTION_EVENT_QUERY_KEYS.getActionEventDetail(id, useNewEndpoint),
    enabled: hasReadEventsPermission && id.length > 0,
    staleTime: 10000,
    refetchInterval: ({ state: { data } }) => {
      if (useNewEndpoint) {
        if (
          data &&
          isCGDOLoadingResponse(data) &&
          dataUpdateCountRef.current < COLD_STORAGE_MAX_RETRIES
        ) {
          return getColdStorageRetryWaitTime(dataUpdateCountRef.current);
        }
      }
      // Do not retry by default
      return false;
    },
  });

  const coldStorageLoading = useMemo(
    () =>
      id
        ? getLoadingFromColdStorageStateForCGDO(
            id,
            query.data && isCGDOLoadingResponse(query.data)
              ? query.data.data_loader
              : undefined,
          )
        : undefined,
    [id, query.data],
  );

  useEffect(() => {
    // Reset update count when the ID changes
    dataUpdateCountRef.current = 0;
  }, [id]);

  // Increase the countRef whenever we get a loading response
  useEffect(() => {
    if (query.data && isCGDOLoadingResponse(query.data)) {
      dataUpdateCountRef.current += 1;
    }
  }, [query.data]);

  const { data, isLoading } = useCGDOProgressiveData(
    query.data,
    query.isLoading,
    query.isError,
  );

  return {
    // This is still accesible if needed for whatever reason
    query,
    // The following values already take into account the cold storage loading state
    data,
    isLoading,
    coldStorageLoading,
  };
};
